// Segment-Steuerung (Handoff Tickets): Behälter --fill2, aktives Segment --bg mit Schatten
// (art „akzent“: --acc-fill). Einzelne Segmente können gesperrt sein (mit Tooltip-Grund).
import type { CSSProperties, KeyboardEvent, ReactNode } from 'react';
import type { Icon } from '@phosphor-icons/react';

export interface SegmentEintrag<K extends string> {
  key: K;
  label: ReactNode;
  icon?: Icon;
  /** Farbe des Icons (z. B. Tab-Farbe im Einsatz-Widget) */
  iconFarbe?: string;
  gesperrt?: boolean;
  tip?: string;
  /** Zahl hinter dem Label */
  anzahl?: number;
}

export interface SegmentProps<K extends string> {
  eintraege: readonly SegmentEintrag<K>[];
  wert: K;
  onChange: (k: K) => void;
  ariaLabel: string;
  /** 44 = Tippfläche auf dem Handy */
  hoehe?: 30 | 32 | 34 | 44;
  art?: 'weich' | 'akzent';
  voll?: boolean;
  /** Nur das aktive Segment trägt Text, die übrigen zeigen Icon + Zahl (Name im Tooltip) —
   *  damit viele Tabs in eine schmale Spalte passen (#533). */
  kompakt?: boolean;
  style?: CSSProperties;
}

export function Segment<K extends string>({ eintraege, wert, onChange, ariaLabel, hoehe = 30, art = 'weich', voll = false, kompakt = false, style }: SegmentProps<K>) {
  const nutzbar = eintraege.filter(e => !e.gesperrt);
  const tastatur = (ev: KeyboardEvent<HTMLDivElement>) => {
    if (ev.key !== 'ArrowRight' && ev.key !== 'ArrowLeft') return;
    ev.preventDefault();
    const i = nutzbar.findIndex(e => e.key === wert);
    const n = nutzbar.length;
    const naechster = nutzbar[(i + (ev.key === 'ArrowRight' ? 1 : n - 1) + n) % n];
    if (naechster) onChange(naechster.key);
  };
  return (
    <div role="tablist" aria-label={ariaLabel} onKeyDown={tastatur}
      className={voll ? 'flex' : 'inline-flex'}
      style={{ padding: 3, gap: 2, borderRadius: hoehe === 30 ? 13 : 15, background: 'var(--fill2)', ...style }}>
      {eintraege.map(e => {
        const an = e.key === wert;
        const I = e.icon;
        const nurIcon = kompakt && !an && !!I;
        const aktiv: CSSProperties = art === 'akzent'
          ? { background: 'var(--acc-fill)', color: 'var(--acc-ink)', boxShadow: 'var(--acc-sh)' }
          : { background: 'var(--bg)', color: 'var(--ink)', boxShadow: '0 2px 8px -3px var(--shade)' };
        return (
          <button key={e.key} type="button" role="tab" aria-selected={an} aria-disabled={e.gesperrt || undefined}
            tabIndex={an ? 0 : -1} data-tip={e.tip}
            aria-label={nurIcon && typeof e.label === 'string' ? e.label : undefined}
            onClick={() => { if (!e.gesperrt) onChange(e.key); }}
            className={`inline-flex items-center justify-center whitespace-nowrap ${e.gesperrt ? 'cursor-not-allowed' : 'cursor-pointer'}`}
            style={{
              flex: voll ? (nurIcon ? '0 1 auto' : 1) : undefined, height: hoehe, padding: nurIcon ? '0 10px' : voll ? '0 6px' : '0 12px', gap: 6, border: 'none', minWidth: 0,
              borderRadius: hoehe === 30 ? 10 : 12, fontFamily: 'inherit', fontSize: 12.5, fontWeight: an ? 800 : 700,
              transition: 'background .2s, color .2s', opacity: e.gesperrt ? 0.4 : 1,
              ...(an ? aktiv : { background: 'transparent', color: 'var(--ink2)' }),
            }}>
            {I && <I size={14} weight={an ? 'fill' : 'bold'} color={e.iconFarbe} />}
            {!nurIcon && <span className="truncate">{e.label}</span>}
            {e.anzahl !== undefined && e.anzahl > 0 && <span className="tabular-nums" style={{ opacity: 0.6 }}>{e.anzahl}</span>}
          </button>
        );
      })}
    </div>
  );
}
